import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DoorOpen, ShieldCheck, Check, X, Clock } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useMeta } from "@/context/MetaContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { StatusChip } from "@/components/StatusChip";
import { ComplianceNotes } from "@/components/ComplianceNotes";
import { formatDateTime } from "@/lib/format";

export function ApprovalResult({ record, approvedLabel = "Approved", rejectedLabel = "Rejected" }) {
  if (!record?.decided_at) return null;
  const approved = record.status === "approved";
  return (
    <div className={`rounded-xl px-4 py-3 ${approved ? "bg-patria-green/10" : "bg-patria-terracottaDeep/10"}`} data-testid="approval-result">
      <div className="flex items-center gap-2">
        {approved ? <Check className="h-4 w-4 text-patria-green" /> : <X className="h-4 w-4 text-patria-terracottaDeep" />}
        <span className={`text-sm font-medium ${approved ? "text-patria-greenDeep" : "text-patria-terracottaDeep"}`}>
          {approved ? approvedLabel : rejectedLabel} by {record.decided_by?.name}
        </span>
        <span className="text-xs text-patria-muted">· {formatDateTime(record.decided_at)}</span>
      </div>
      {record.reason && <p className="text-sm text-patria-ink mt-1">"{record.reason}"</p>}
    </div>
  );
}

export function ApprovalActions({ caseId, endpoint, queryKey, label }) {
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  const decide = useMutation({
    mutationFn: async (decision) => api.post(endpoint, { decision, reason }),
    onSuccess: (_, decision) => {
      toast.success(decision === "approved" ? `${label} approved` : `${label} rejected`);
      qc.invalidateQueries({ queryKey });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="rounded-xl bg-patria-sage/40 p-4 space-y-3" data-testid="approval-actions">
      <p className="text-sm font-medium text-patria-ink">People Operations decision</p>
      {rejecting ? (
        <div className="space-y-2">
          <Label className="text-xs text-patria-muted">Reason for rejection (required)</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="reject-reason" className="bg-patria-surface border-patria-muted/30" placeholder="Explain what needs to change…" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setRejecting(false)} className="rounded-full border-patria-muted/30" data-testid="reject-cancel">Cancel</Button>
            <Button size="sm" onClick={() => decide.mutate("rejected")} disabled={!reason.trim() || decide.isPending} className="rounded-full bg-patria-terracottaDeep text-patria-surface" data-testid="reject-confirm">Confirm rejection</Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => decide.mutate("approved")} disabled={decide.isPending} className="rounded-full bg-patria-green hover:bg-patria-greenDeep text-patria-surface" data-testid="approve-button"><Check className="h-4 w-4 mr-1.5" /> Approve</Button>
          <Button size="sm" variant="outline" onClick={() => setRejecting(true)} className="rounded-full border-patria-terracottaDeep/40 text-patria-terracottaDeep" data-testid="reject-button"><X className="h-4 w-4 mr-1.5" /> Reject</Button>
        </div>
      )}
    </div>
  );
}

export function EntryRequestSection({ c, me }) {
  const qc = useQueryClient();
  const { data: meta } = useMeta();
  const entry = c.entry_request;
  const [criteria, setCriteria] = useState((meta?.entry_criteria || []).map(() => false));
  const [attest, setAttest] = useState(false);
  const [note, setNote] = useState("");

  const canRequest = me.role === "manager" && ["feedback_logged", "entry_rejected"].includes(c.status);
  const canDecide = ["people_ops", "leadership"].includes(me.role) && entry?.status === "pending" && entry?.requested_by?.id !== me.id;

  const submit = useMutation({
    mutationFn: async () => api.post(`/cases/${c.id}/entry-request`, { criteria, anti_retaliation: attest, note }),
    onSuccess: () => { toast.success("Entry request submitted to People Operations"); qc.invalidateQueries({ queryKey: ["case", c.id] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  const allChecked = criteria.length > 0 && criteria.every(Boolean) && attest;

  return (
    <Card className="bg-patria-surface border-patria-muted/15 p-6" data-testid="entry-section">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-patria-terracotta/10 flex items-center justify-center"><DoorOpen className="h-4 w-4 text-patria-terracotta" /></div>
          <div>
            <h3 className="font-heading font-semibold text-patria-ink">Stage 1 · Entry Request & People Ops Approval</h3>
            <p className="text-xs text-patria-muted">The gate — People Operations must approve entry before a plan can be built.</p>
          </div>
        </div>
        {entry && <StatusChip label={entry.status === "pending" ? "Pending approval" : entry.status === "approved" ? "Approved" : "Rejected"} tone={entry.status === "pending" ? "amber" : entry.status === "approved" ? "green" : "red"} testId="entry-status" />}
      </div>

      {/* Existing request view */}
      {entry && (
        <div className="space-y-3">
          <div className="grid gap-1.5">
            {meta?.entry_criteria?.map((crit, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-patria-ink"><Check className="h-4 w-4 text-patria-green mt-0.5 shrink-0" />{crit}</div>
            ))}
            <div className="flex items-start gap-2 text-sm text-patria-ink"><ShieldCheck className="h-4 w-4 text-patria-green mt-0.5 shrink-0" />{meta?.compliance?.anti_retaliation}</div>
          </div>
          {entry.note && <p className="text-sm text-patria-muted italic">Manager note: {entry.note}</p>}
          <p className="text-xs text-patria-muted">Requested by {entry.requested_by?.name} · {formatDateTime(entry.requested_at)}</p>
          <ApprovalResult record={entry} approvedLabel="Entry approved" rejectedLabel="Entry rejected" />
          {entry.status === "pending" && !canDecide && ["people_ops", "leadership"].includes(me.role) === false && (
            <p className="inline-flex items-center gap-1.5 text-sm text-patria-terracottaDeep"><Clock className="h-4 w-4" /> Awaiting People Operations review.</p>
          )}
          {canDecide && <ApprovalActions caseId={c.id} endpoint={`/cases/${c.id}/entry-request/decision`} queryKey={["case", c.id]} label="Entry" />}
        </div>
      )}

      {/* New request form */}
      {!entry && canRequest && (
        <div className="space-y-4">
          <p className="text-sm text-patria-muted">Confirm all entry criteria are true. A manager cannot self-approve — this routes to People Operations.</p>

          <div className="rounded-xl bg-patria-sage/40 p-4">
            <p className="text-xs font-semibold text-patria-muted uppercase tracking-wide mb-2">Before you enter someone into COMPASS</p>
            <ComplianceNotes only={["two_track", "protected_leave", "at_will", "confidentiality"]} />
          </div>

          <div className="space-y-2.5">
            {meta?.entry_criteria?.map((crit, i) => (
              <label key={i} className="flex items-start gap-3 cursor-pointer" data-testid={`entry-criteria-${i}`}>
                <Checkbox checked={criteria[i]} onCheckedChange={(v) => setCriteria((arr) => arr.map((x, j) => (j === i ? !!v : x)))} className="mt-0.5 data-[state=checked]:bg-patria-green data-[state=checked]:border-patria-green" />
                <span className="text-sm text-patria-ink">{crit}</span>
              </label>
            ))}
          </div>
          <label className="flex items-start gap-3 cursor-pointer rounded-xl bg-patria-terracotta/5 border border-patria-terracotta/20 p-3" data-testid="entry-attestation">
            <Checkbox checked={attest} onCheckedChange={(v) => setAttest(!!v)} className="mt-0.5 data-[state=checked]:bg-patria-terracotta data-[state=checked]:border-patria-terracotta" />
            <span className="text-sm text-patria-ink"><span className="font-medium">Anti-retaliation attestation:</span> {meta?.compliance?.anti_retaliation}</span>
          </label>
          <div className="space-y-1.5">
            <Label className="text-patria-ink">Note to People Operations (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="entry-note" className="bg-patria-surface border-patria-muted/30" placeholder="Any context for the reviewer…" />
          </div>
          <div className="flex justify-end">
            <Button onClick={() => submit.mutate()} disabled={!allChecked || submit.isPending} data-testid="entry-submit" className="rounded-full bg-patria-terracotta hover:bg-patria-terracottaDeep text-patria-surface">Submit entry request</Button>
          </div>
        </div>
      )}

      {!entry && !canRequest && <p className="text-sm text-patria-muted">Entry has not been requested yet.</p>}
    </Card>
  );
}
